// Copyright (c) 2019-2023 Cigent Technology Inc. All Rights Reserved.
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { ID3EPolicy, IKnownGoodApp } from 'src/types/d3ePolicy';
import { MatDialog } from '@angular/material/dialog';
import { UploadDialogComponent } from '../upload-dialog/upload-dialog.component';
import { AlertDialogComponent } from 'src/app/dialogs/alert/alert.component';
import { NotificationService } from 'src/services/notification-service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-policy-safe-app',
    templateUrl: './policy-safe-app.component.html',
    styleUrls: ['./policy-safe-app.component.scss'],
    standalone: false
})
export class PolicySafeAppComponent implements OnInit {
  @Input() d3ePolicy: ID3EPolicy;
  @Input() app: IKnownGoodApp;
  @Output() changed: EventEmitter<void> = new EventEmitter<void>();
  @Output() deleteApp: EventEmitter<IKnownGoodApp> = new EventEmitter<IKnownGoodApp>();
  @Output() toggle: EventEmitter<PolicySafeAppComponent> = new EventEmitter<PolicySafeAppComponent>();
  @ViewChild('displayNameInput', { read: ElementRef }) displayNameInput: ElementRef;
  collapsed = true;
  dupeCertificate = false;

  constructor(
    public dialog: MatDialog,
    private notification: NotificationService,
    private translate: TranslateService,
    private changeDetectorRef: ChangeDetectorRef,
  ) { }

  ngOnInit() {
  }

  setDisplayNameFocus() {
    this.displayNameInput.nativeElement.focus();
  }

  upload() {
    const dialogRef = this.dialog.open(UploadDialogComponent, {
      width: '450px',
      height: '200px',
      data: {},
    });
    dialogRef.afterClosed().subscribe(response => {

      if (response?.strength < 2) {
        const vals = this.translate.instant(['NO', 'YES', 'CERT_EXPIRED', 'CERT_WEAK']);
        let message = vals['CERT_EXPIRED'];
        if (response.strength == 1) {
          message = vals['CERT_WEAK'];
        }

        const alertDialogRef = this.dialog.open(AlertDialogComponent, {
          width: '450px',
          data: {
            title: '',
            content: this.translate.instant('CONSIDER_APP_SAFE', {message}),
            rejectBtnName: vals['NO'],
            acceptBtnName: vals['YES'],
            showRejectBtn: true,
          },
        });
        alertDialogRef.afterClosed().subscribe(result => {
          if (result) {
            this.updateAppFromUpload(response);
          }
        });
      } else {
        this.updateAppFromUpload(response);
      }
    });
  }

  updateAppFromUpload(response) {
    this.app = Object.assign(this.app, response);
    if (!this.app.displayName || this.app.displayName === '') {
      this.app.displayName = response.subject.CN;
    }
    this.changed.emit();
    this.isDupeCertificate();
    this.changeDetectorRef.detectChanges();
  }

  getUploadButtonText() {
    if (this.app.subject.CN) {
      return this.translate.instant('RE_UPLOAD_CERT');
    }
    return this.translate.instant('UPLOAD_CERT');
  }

  keyAlphaNum(event: any) {
    this.collapsed = false;
    if (!event?.key?.match(/[a-zA-Z0-9]/)) {
      event.preventDefault();
    }
    this.changed.emit();
  }
  
  isDupeName() {
    const displayName = this.app.displayName.toLowerCase();
    let count = 0;
    this.d3ePolicy.knownGood.forEach(knownApp => {
      const knownDisplayName = knownApp.displayName.toLowerCase();
      if (displayName.includes(knownDisplayName)) {
        count++;
      }
    });
    if (count > 1) {
      this.showDupeWarning();
    }
  }

  isDupeCertificate() {
    let count = 0;
    this.d3ePolicy.knownGood.forEach(knownApp => {
      if (JSON.stringify(this.app.subject) === JSON.stringify(knownApp.subject)) {
        count++;
      }
      this.app.paths.forEach(path => {
        knownApp.paths.forEach(knownAppPath => {
          if (path.toLocaleLowerCase() === knownAppPath.toLocaleLowerCase()){
            count++;
          }
        })
      });
    });
    if (count > 1) {
      this.dupeCertificate = true;
      this.showDupeWarning();
    }
    else {
      this.dupeCertificate = false;
    }
  }

  showDupeWarning() {
    this.notification.showWarning(this.translate.instant('PATH_LIST_ERROR_SIMILAR_APP'));
  }

  toggleCollapsed() {
    this.toggle.emit(this);
  }

  collapse() {
    this.collapsed = true;
  }

  expand() {
    this.collapsed = false;
  }
}
